import { Add } from '@mui/icons-material';
import { AppBar, Box, Tab, Tabs } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
// Fecth
import { getAddressBook } from '../../../fetch';
// Models
import { IAddressBookRes } from '../../../models';
// Components
import {
  Loader,
  Page,
  PageTitleWithButton,
  Pagination,
  Button as SMButton
} from '../../../components';
import { AddressBook } from './address-book';
import { IncentiveTypes } from './incentive-types';
import { EmailTemplates } from './email-templates';
import { AddressBookModal } from './modals';
import { SalesManagementFilters } from './sales-management-filters';
import { PartAbbreviations } from './part-abbreviations';

export const enum salesManagementPages {
  incentiveRates,
  addressBook,
  emailTemplates,
  partAbbreviations
}

interface ISalesManagement {}

export const SalesManagement: FC<ISalesManagement> = () => {
  const classes = useStyles();
  const [selectedPage, setSelectedPage] = useState<salesManagementPages>(
    salesManagementPages.incentiveRates
  );

  // Filters //

  const [nameSearch, setNameSearch] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<number[]>([]);
  const [appliedNeighborhoods, setAppliedNeighborhoods] = useState<number[]>([]);
  const [selectedTerritories, setSelectedTerritories] = useState<number[]>([]);
  const [appliedTerritories, setAppliedTerritories] = useState<number[]>([]);
  const [isGlobal, setIsGlobal] = useState<string | null>(null);
  const [appliedIsGlobal, setAppliedIsGlobal] = useState<string | undefined>(undefined);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState<boolean>(false);
  const [selectedSort, setSelectedSort] = useState<string>('DisplayName');
  const [sortDirection, setSortDirection] = useState<{
    DisplayName?: 'Asc' | 'Desc';
    IsGlobal?: 'Asc' | 'Desc';
    UpdatedDate?: 'Asc' | 'Desc';
  }>({
    DisplayName: 'Asc'
  });

  useEffect(() => {
    setHasAppliedFilters(
      nameSearch !== '' ||
        selectedNeighborhoods.length > 0 ||
        selectedTerritories.length > 0 ||
        isGlobal !== null
    );
  }, [nameSearch, selectedNeighborhoods, selectedTerritories, isGlobal]);

  const handleSort = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  // Modal Functions //

  const [isModalOpenIncentiveType, setIsModalOpenIncentiveType] = useState<boolean>(false);
  const [isModalOpenAddressBook, setIsModalOpenAddressBook] = useState<boolean>(false);
  const [isModalOpenEmailTemplate, setIsModalOpenEmailTemplate] = useState<boolean>(false);
  const [isModalOpenPartAbbreviation, setIsModalOpenPartAbbreviation] = useState<boolean>(false);

  // Pagination //

  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);

  // Incentive Types //
  const handleAddIncentiveType = () => {
    setIsModalOpenIncentiveType(true);
  };

  // Address Book //
  const handleApplyFilters = () => {
    setPage(0);
    setIsApplyingFilters(true);
    setAppliedSearch(nameSearch);
    setAppliedIsGlobal(isGlobal);
    setAppliedNeighborhoods(selectedNeighborhoods);
    setAppliedTerritories(selectedTerritories);
    setTimeout(() => {
      // Simulate delay for loading state visibility
      setIsApplyingFilters(false);
    }, 1000);
  };

  const handleClearFilters = () => {
    setNameSearch('');
    setAppliedSearch('');
    setIsGlobal(null);
    setAppliedIsGlobal(undefined);
    setSelectedNeighborhoods([]);
    setAppliedNeighborhoods([]);
    setSelectedTerritories([]);
    setAppliedTerritories([]);
    refetchAddressBook();
  };

  const {
    isLoading: isLoadingAddressBook,
    data: addressBookData,
    refetch: refetchAddressBook
  } = useQuery<IAddressBookRes, Error>(
    [
      'addressBook',
      page,
      perPage,
      appliedSearch,
      appliedIsGlobal,
      ...appliedNeighborhoods,
      ...appliedTerritories,
      selectedSort,
      sortDirection[selectedSort]
    ],
    () => {
      const requestPayload: any = {
        page: page + 1,
        perPage,
        name: appliedSearch,
        isGlobal:
          appliedIsGlobal === undefined || appliedIsGlobal === null
            ? undefined
            : appliedIsGlobal === 'true',
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort]
      };
      // Add neighborhoodIds as separate parameters
      appliedNeighborhoods.forEach((id, index) => {
        requestPayload[`neighborhoodIds[${index}]`] = id;
      });
      appliedTerritories.forEach((id, index) => {
        requestPayload[`territoryIds[${index}]`] = id;
      });
      return getAddressBook(requestPayload);
    },
    {
      keepPreviousData: true,
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      onError: error => {
        console.error('API Error:', error);
      }
    }
  );

  const handleAddContact = () => {
    setIsModalOpenAddressBook(true);
  };

  const handleAddEmailTemplate = () => {
    setIsModalOpenEmailTemplate(true);
  };

  const handleAddPartAbbreviation = () => {
    setIsModalOpenPartAbbreviation(true);
  };

  // Tab functions //

  const salesManagementLinks = useMemo(
    () => [
      {
        name: 'Incentive Types',
        value: salesManagementPages.incentiveRates
      },
      {
        name: 'Address Book',
        value: salesManagementPages.addressBook
      },
      {
        name: 'Email Templates',
        value: salesManagementPages.emailTemplates
      },
      {
        name: 'Option Title Casing',
        value: salesManagementPages.partAbbreviations
      }
    ],
    []
  );

  const title = 'Sales Management';

  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: salesManagementPages) => {
    setSelectedPage(newValue);
  };

  return (
    <Page title={title}>
      <Box position="relative">
        <PageTitleWithButton
          pageTitleBackProps={{
            showDivider: false,
            backText: 'Back to Sales Dashboard',
            backUrl: '/sales',
            title: title
          }}
          button={
            selectedPage === salesManagementPages.incentiveRates ? (
              <SMButton
                id="create-incentive-type"
                startIcon={<Add />}
                onClick={handleAddIncentiveType}
                color="primary"
                variant="contained"
                className={classes.addRoleButton}
              >
                Add Incentive Type
              </SMButton>
            ) : selectedPage === salesManagementPages.addressBook ? (
              <SMButton
                id="add-contact"
                startIcon={<Add />}
                onClick={handleAddContact}
                color="primary"
                variant="contained"
                className={classes.addRoleButton}
              >
                Add Contact
              </SMButton>
            ) : selectedPage === salesManagementPages.emailTemplates ? (
              <SMButton
                id="add-email-template"
                startIcon={<Add />}
                onClick={handleAddEmailTemplate}
                color="primary"
                variant="contained"
                className={classes.addRoleButton}
              >
                Add Email Template
              </SMButton>
            ) : selectedPage === salesManagementPages.partAbbreviations ? (
              <SMButton
                id="add-part-abbreviation"
                startIcon={<Add />}
                onClick={handleAddPartAbbreviation}
                color="primary"
                variant="contained"
                className={classes.addRoleButton}
              >
                Add Option
              </SMButton>
            ) : null
          }
        />
        <AppBar position="static" color="default" component="div" className={classes.tabBar}>
          <Tabs
            value={selectedPage}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleChangeTab}
            variant="scrollable"
          >
            {salesManagementLinks.map(link => (
              <Tab
                value={link.value}
                key={link.name}
                id={`${link.name}-information`}
                aria-controls={`${link.name}-information`}
                label={link.name}
              />
            ))}
          </Tabs>
        </AppBar>
        {selectedPage === salesManagementPages.incentiveRates && (
          <IncentiveTypes
            isModalOpen={isModalOpenIncentiveType}
            setModalOpen={setIsModalOpenIncentiveType}
          />
        )}
        {selectedPage === salesManagementPages.addressBook && (
          <>
            {isLoadingAddressBook ? (
              <Loader position="centered" type="overlay" />
            ) : (
              <>
                <Box mt={1}>
                  <SalesManagementFilters
                    isLoadingAddressBook={isLoadingAddressBook || isApplyingFilters}
                    nameSearch={nameSearch}
                    setNameSearch={setNameSearch}
                    isGlobal={isGlobal}
                    setIsGlobal={setIsGlobal}
                    selectedNeighborhoods={selectedNeighborhoods}
                    setSelectedNeighborhoods={setSelectedNeighborhoods}
                    selectedTerritories={selectedTerritories}
                    setSelectedTerritories={setSelectedTerritories}
                    handleApplyFilters={handleApplyFilters}
                    handleClearFilters={handleClearFilters}
                    hasAppliedFilters={hasAppliedFilters}
                  />
                  <AddressBook
                    refreshData={refetchAddressBook}
                    addressBookData={addressBookData}
                    selectedSort={selectedSort}
                    sortDirection={sortDirection}
                    handleSort={handleSort}
                    isLoadingAddressBook={isLoadingAddressBook || isApplyingFilters}
                  />
                  <Pagination
                    page={page}
                    count={recordCount}
                    rowsPerPage={perPage}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                  />
                  <AddressBookModal
                    isOpen={isModalOpenAddressBook}
                    onClose={() => {
                      setIsModalOpenAddressBook(false);
                    }}
                    refreshData={refetchAddressBook}
                  />
                </Box>
              </>
            )}
          </>
        )}
        {selectedPage === salesManagementPages.emailTemplates && (
          <EmailTemplates
            isModalOpen={isModalOpenEmailTemplate}
            setModalOpen={setIsModalOpenEmailTemplate}
          />
        )}
        {selectedPage === salesManagementPages.partAbbreviations && (
          <PartAbbreviations
            isModalOpen={isModalOpenPartAbbreviation}
            setModalOpen={setIsModalOpenPartAbbreviation}
          />
        )}
      </Box>
    </Page>
  );
};

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  addRoleButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
